import React, { useState, useRef } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { api, Mark } from '../api';
import { GREEN_PLACEHOLDER } from '../components';
import { ImagePickerModal } from './ImagePickerModal';
import { DraggablePositionMap } from './DraggablePositionMap';

export function EditMarkModal({mark,onClose,onSaved}:{mark:Mark;onClose:()=>void;onSaved:(m:Mark)=>void}){
  const {mode}=useThemeMode();const T=getTheme(mode);
  const iS:React.CSSProperties={width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 14px',color:T.text,fontSize:14,outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const lS:React.CSSProperties={display:'block',color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6};
  const bP:React.CSSProperties={background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'12px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'};
  const bSc:React.CSSProperties={background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:5,padding:'12px 24px',fontSize:14,cursor:'pointer',fontFamily:'inherit'};
  const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};
  const secT:React.CSSProperties={color:T.textMuted,fontSize:11,fontWeight:600,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:16};

  const[form,setForm]=useState({
    name:mark.name,description:mark.description||'',
    areaHa:String(mark.areaHa||''),county:mark.county||'',municipality:mark.municipality||'',
    address:mark.address||'',contactEmail:mark.contactEmail||'',contactPhone:mark.contactPhone||'',
    pricePerYear:String(mark.pricePerYear||''),
    gameTypesJson:mark.gameTypesJson||'[]',
    difficulty:mark.difficulty||'',
    highlightsJson:mark.highlightsJson||'[]',
    includesJson:mark.includesJson||'[]',
    annualDrivenHunts:String(mark.annualDrivenHunts||''),
    afterhuntHuntersCount:String(mark.afterhuntHuntersCount||''),
    contactName:mark.contactName||'',
    mapLat:String(mark.mapLat||''),
    mapLng:String(mark.mapLng||''),
    featured:mark.featured||false,
  });
  const[specOcc,setSpecOcc]=useState<Record<string,number>>(()=>{try{return JSON.parse(mark.speciesOccurrenceJson||'{}');}catch{return {};}});
  const[facilities,setFacilities]=useState<Record<string,number>>(()=>{try{return JSON.parse(mark.facilitiesJson||'{}');}catch{return {};}});
  const[galleryUrls,setGalleryUrls]=useState<string[]>(mark.galleryUrls||[]);
  const[videoUrl,setVideoUrl]=useState(mark.videoUrl||'');
  const[imageUrl,setImageUrl]=useState(mark.imageUrl||'');
  const[saving,setSaving]=useState(false);
  const[error,setError]=useState('');
  const[uploading,setUploading]=useState(false);
  const[showImgPicker,setShowImgPicker]=useState(false);
  const[showGalleryPicker,setShowGalleryPicker]=useState(false);
  const imageRef=useRef<HTMLInputElement>(null);
  const galleryRef=useRef<HTMLInputElement>(null);
  const videoRef=useRef<HTMLInputElement>(null);

  const handleMainImage=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploading(true);
    try{const res=await api.uploadFile(file);setImageUrl(res.url)}
    catch{setError('Bilduppladdning misslyckades')}
    finally{setUploading(false);e.target.value=''}
  };
  const handleGallery=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=e.target.files;if(!files?.length)return;
    setUploading(true);
    try{const res=await api.uploadFiles(Array.from(files));setGalleryUrls(prev=>[...prev,...res.files.map(f=>f.url)])}
    catch{setError('Bilduppladdning misslyckades')}
    finally{setUploading(false);e.target.value=''}
  };
  const handleVideo=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploading(true);
    try{const res=await api.uploadFile(file);setVideoUrl(res.url)}
    catch{setError('Videouppladdning misslyckades')}
    finally{setUploading(false);e.target.value=''}
  };
  const removeGalleryImg=(idx:number)=>setGalleryUrls(prev=>prev.filter((_,i)=>i!==idx));

  const handleSave=async()=>{
    setSaving(true);setError('');
    try{
      const input:any={
        name:form.name,description:form.description||undefined,
        areaHa:form.areaHa?parseFloat(form.areaHa):undefined,
        county:form.county||undefined,municipality:form.municipality||undefined,
        address:form.address||undefined,contactEmail:form.contactEmail||undefined,
        contactPhone:form.contactPhone||undefined,
        imageUrl:imageUrl||undefined,
        galleryUrls:galleryUrls.length?galleryUrls:undefined,
        videoUrl:videoUrl||undefined,
        gameTypesJson:form.gameTypesJson||undefined,
        difficulty:form.difficulty||undefined,
        highlightsJson:form.highlightsJson||undefined,
        includesJson:form.includesJson||undefined,
        speciesOccurrenceJson:Object.keys(specOcc).length?JSON.stringify(specOcc):undefined,
        facilitiesJson:Object.keys(facilities).length?JSON.stringify(facilities):undefined,
        annualDrivenHunts:form.annualDrivenHunts?parseInt(form.annualDrivenHunts):undefined,
        afterhuntHuntersCount:form.afterhuntHuntersCount?parseInt(form.afterhuntHuntersCount):undefined,
        contactName:form.contactName||undefined,
        mapLat:form.mapLat?parseFloat(form.mapLat):undefined,
        mapLng:form.mapLng?parseFloat(form.mapLng):undefined,
        featured:form.featured||undefined,
      };
      const updated=await api.updateMark(mark.id,input);
      onSaved(updated);onClose();
    }catch(ex:unknown){setError(ex instanceof Error?ex.message:'Något gick fel')}
    finally{setSaving(false)}
  };

  const u=(k:string,v:string)=>setForm(f=>({...f,[k]:v}));
  const fld=(label:string,key:string,opts?:{type?:string;rows?:number})=>(
    <div style={{marginBottom:14}}>
      <label style={lS}>{label}</label>
      {opts?.rows
        ?<textarea value={(form as any)[key]} onChange={e=>u(key,e.target.value)} rows={opts.rows} style={{...iS,resize:'vertical'}}/>
        :<input type={opts?.type||'text'} value={(form as any)[key]} onChange={e=>u(key,e.target.value)} style={iS}/>
      }
    </div>
  );

  return(
    <div style={{position:'fixed',inset:0,background:T.overlay,zIndex:950,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget)onClose()}}>
      <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,width:'100%',maxWidth:680,maxHeight:'94vh',overflow:'auto',position:'relative'}}>
        <div style={{position:'sticky',top:0,background:T.bg,borderBottom:`1px solid ${T.border}`,padding:'16px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',zIndex:10}}>
          <h2 style={{color:T.text,fontSize:18,fontWeight:800,margin:0}}>Redigera jaktmark</h2>
          <button onClick={onClose} style={{background:'rgba(0,0,0,0.3)',border:'none',color:T.text,fontSize:18,width:36,height:36,borderRadius:'50%',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>x</button>
        </div>
        <div style={{padding:'20px 24px 28px'}}>
          {error&&<div style={{background:'rgba(192,57,43,0.15)',border:'1px solid rgba(192,57,43,0.3)',borderRadius:4,padding:'10px 14px',color:'#ff6b6b',fontSize:13,marginBottom:16}}>{error}</div>}
          {uploading&&<div style={{background:T.amberDim,border:'1px solid rgba(238,143,9,0.25)',borderRadius:4,padding:'10px 14px',color:T.amber,fontSize:13,marginBottom:16}}>Laddar upp fil...</div>}

          <div style={secT}>Grundinfo</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            {fld('Namn *','name')}
            {fld('Beskrivning','description',{rows:4})}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Areal (ha)','areaHa',{type:'number'})}</div>
              <div>{fld('Kontakt-epost','contactEmail',{type:'email'})}</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:4}}>
              <div style={{gridColumn:'1 / -1',padding:'10px 12px',borderRadius:8,background:T.amberDim,color:T.text,fontSize:13}}>
                Priser, fällavgifter och kött sätts per grupp på markens Ekonomi-sida (Min jakt → marken → Ekonomi) och visas automatiskt i annonserna.
              </div>
              <div>
                <label style={lS}>Viltslag (kommaseparerat)</label>
                <input value={(()=>{try{return JSON.parse(form.gameTypesJson||'[]').join(', ')}catch{return ''}})()}
                  onChange={e=>u('gameTypesJson',JSON.stringify(e.target.value.split(',').map((s:string)=>s.trim()).filter(Boolean)))}
                  placeholder="ex. älg, dovhjort" style={iS}/>
              </div>
            </div>
          </div>

          <div style={secT}>Plats & Karta</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Län','county')}</div>
              <div>{fld('Kommun','municipality')}</div>
            </div>
            {fld('Adress','address')}
            <DraggablePositionMap lat={form.mapLat} lng={form.mapLng} onChange={(la,lo)=>{u('mapLat',la);u('mapLng',lo);}}/>
          </div>

          <div style={secT}>Jaktinfo</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>
                <label style={lS}>Svårighetsgrad</label>
                <select value={form.difficulty} onChange={e=>u('difficulty',e.target.value)} style={iS}>
                  <option value="">Välj...</option>
                  <option value="Lätt">Lätt</option>
                  <option value="Medel">Medel</option>
                  <option value="Svår">Svår</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8,paddingTop:20}}>
                <input type="checkbox" checked={form.featured as boolean} onChange={e=>setForm(f=>({...f,featured:e.target.checked}))} style={{width:18,height:18,accentColor:T.amber}}/>
                <label style={{color:T.text,fontSize:13,fontWeight:600}}>Utvald mark</label>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Kontaktperson','contactName')}</div>
              <div>{fld('Telefon','contactPhone',{type:'tel'})}</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Drevjakter / år','annualDrivenHunts',{type:'number'})}</div>
              <div>{fld('Eftersöksjägare','afterhuntHuntersCount',{type:'number'})}</div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={lS}>Highlights (kommaseparerat)</label>
              <input value={(()=>{try{return JSON.parse(form.highlightsJson||'[]').join(', ')}catch{return ''}})()}
                onChange={e=>u('highlightsJson',JSON.stringify(e.target.value.split(',').map((s:string)=>s.trim()).filter(Boolean)))}
                placeholder="ex. Stora arealer, Bra vilttillgång, Nära Stockholm" style={iS}/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={lS}>Ingår (kommaseparerat)</label>
              <input value={(()=>{try{return JSON.parse(form.includesJson||'[]').join(', ')}catch{return ''}})()}
                onChange={e=>u('includesJson',JSON.stringify(e.target.value.split(',').map((s:string)=>s.trim()).filter(Boolean)))}
                placeholder="ex. Jaktradio, Jakthund, Eftersökstjänst" style={iS}/>
            </div>
          </div>

          <div style={secT}>Viltförekomst</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'8px 16px'}}>
              {['älg','dovhjort','kronhjort','rådjur','vildsvin','fasan','björn','lo','räv','hare','and','kanadagås','grågås'].map(sp=>{
                const nm:Record<string,string>={'älg':'Älg','dovhjort':'Dovhjort','kronhjort':'Kronhjort','rådjur':'Rådjur','vildsvin':'Vildsvin','fasan':'Fasan','björn':'Björn','lo':'Lo','räv':'Räv','hare':'Hare','and':'And','kanadagås':'Kanadagås','grågås':'Grågås'};
                const val=specOcc[sp]||0;
                return(<div key={sp} style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{color:T.text,fontSize:12,minWidth:80}}>{nm[sp]||sp}</span>
                  <div style={{display:'flex',gap:3,flex:1}}>
                    {[1,2,3,4,5].map(n=>(
                      <div key={n} onClick={()=>setSpecOcc(prev=>{const nv={...prev};if(nv[sp]===n)delete nv[sp];else nv[sp]=n;return nv})}
                        style={{flex:1,height:12,borderRadius:2,cursor:'pointer',background:n<=val?(['','#5a5a5a','#8a7a30','#c5870a','#e67e22','#c0392b'][val]||T.amber):T.border,transition:'background 0.15s',border:`1px solid ${T.border}`}}/>
                    ))}
                  </div>
                  <span style={{color:T.textMuted,fontSize:10,minWidth:16,textAlign:'right'}}>{val||'-'}</span>
                </div>);
              })}
            </div>
            <div style={{color:T.textMuted,fontSize:10,marginTop:8}}>Klicka för att sätta nivå 1-5. Klicka igen på samma nivå för att ta bort.</div>
          </div>

          <div style={secT}>Faciliteter</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:8}}>
              {[['slaktrum','Slaktrum'],['kylrum','Kylrum'],['eldstad','Eldstad'],['atlar','Åtlar'],['viltkameror','Viltkameror'],['jaktkojor','Jaktkoja'],['pass_torn','Pass m. torn'],['pass_stol','Pass m. stol'],['markpass','Markpass'],['overnattning','Övernattning'],['bastu','Bastu'],['badtunna','Badtunna']].map(([key,label])=>{
                const val=facilities[key]||0;
                return(<div key={key} style={{display:'flex',alignItems:'center',gap:8,background:val>0?'rgba(238,143,9,0.08)':T.surface,border:`1px solid ${val>0?'rgba(238,143,9,0.25)':T.border}`,borderRadius:4,padding:'8px 10px',cursor:'pointer'}} onClick={()=>setFacilities(prev=>{const nv={...prev};if(nv[key])delete nv[key];else nv[key]=1;return nv})}>
                  <div style={{width:18,height:18,borderRadius:3,border:`2px solid ${val>0?T.amber:T.border}`,background:val>0?T.amber:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>
                    {val>0&&<span style={{color:'#000',fontSize:12,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{color:T.text,fontSize:12,fontWeight:val>0?600:400}}>{label}</span>
                </div>);
              })}
            </div>
          </div>

          <div style={secT}>Media</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            {showImgPicker&&<ImagePickerModal markId={mark.id} onPick={urls=>{setImageUrl(urls[0]);setShowImgPicker(false)}} onUpload={()=>{setShowImgPicker(false);imageRef.current?.click()}} onClose={()=>setShowImgPicker(false)}/>}
            {showGalleryPicker&&<ImagePickerModal markId={mark.id} multiple onPick={urls=>{setGalleryUrls(p=>[...p,...urls.filter(x=>!p.includes(x))]);setShowGalleryPicker(false)}} onUpload={()=>{setShowGalleryPicker(false);galleryRef.current?.click()}} onClose={()=>setShowGalleryPicker(false)}/>}
            <div style={{marginBottom:16}}>
              <label style={lS}>Huvudbild</label>
              <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                {imageUrl&&<img src={imageUrl} alt="Huvudbild" style={{width:120,height:80,objectFit:'cover',borderRadius:4,border:`1px solid ${T.border}`}}/>}
                <div style={{flex:1}}>
                  <input value={imageUrl} onChange={e=>setImageUrl(e.target.value)} placeholder="Bild-URL" style={{...iS,marginBottom:8}}/>
                  <input ref={imageRef} type="file" accept="image/*" onChange={handleMainImage} style={{display:'none'}}/>
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={()=>imageRef.current?.click()} style={{...bSc,padding:'8px 16px',fontSize:12}}>Ladda upp bild</button>
                    <button onClick={()=>setShowImgPicker(true)} style={{...bSc,padding:'8px 16px',fontSize:12,background:T.amberDim,border:`1px solid ${T.amber}`}}>Markens album</button>
                  </div>
                </div>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={lS}>Galleri</label>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8}}>
                {galleryUrls.map((url,i)=>(
                  <div key={i} style={{position:'relative',width:80,height:60}}>
                    <img src={url} alt={`Galleri ${i+1}`} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:3,border:`1px solid ${T.border}`}}/>
                    <button onClick={()=>removeGalleryImg(i)} style={{position:'absolute',top:-6,right:-6,background:T.danger,color:T.onDanger,border:'none',width:20,height:20,borderRadius:'50%',fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>x</button>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:8}}>
                <input ref={galleryRef} type="file" accept="image/*" multiple onChange={handleGallery} style={{display:'none'}}/>
                <button onClick={()=>galleryRef.current?.click()} style={{...bSc,padding:'8px 16px',fontSize:12}}>+ Ladda upp bilder</button>
                <button onClick={()=>setShowGalleryPicker(true)} style={{...bSc,padding:'8px 16px',fontSize:12,background:T.amberDim,border:`1px solid ${T.amber}`}}>+ Från markens album</button>
              </div>
            </div>
            <div>
              <label style={lS}>Video</label>
              {videoUrl&&<div style={{marginBottom:8}}><video src={videoUrl} controls style={{width:'100%',maxHeight:180,borderRadius:4,border:`1px solid ${T.border}`}}/></div>}
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <input value={videoUrl} onChange={e=>setVideoUrl(e.target.value)} placeholder="Video-URL" style={{...iS,flex:1}}/>
                <input ref={videoRef} type="file" accept="video/*" onChange={handleVideo} style={{display:'none'}}/>
                <button onClick={()=>videoRef.current?.click()} style={{...bSc,padding:'8px 16px',fontSize:12,whiteSpace:'nowrap'}}>Ladda upp</button>
                {videoUrl&&<button onClick={()=>setVideoUrl('')} style={{background:'transparent',border:'none',color:T.danger,fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>Ta bort</button>}
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:12,justifyContent:'flex-end'}}>
            <button onClick={onClose} style={{...bSc,padding:'12px 24px'}}>Avbryt</button>
            <button onClick={handleSave} disabled={saving||uploading} style={{...bP,padding:'12px 32px',opacity:(saving||uploading)?0.7:1,cursor:(saving||uploading)?'default':'pointer'}}>{saving?'Sparar...':'Spara ändringar'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
